import React from 'react';
import { useSelector } from 'react-redux';
import {
  Ad,
  AppContent,
  baseUnit,
  DisableMainCategory,
  ErrorBoundary,
  PaginationButtonGroup,
  SecondarySectionTitle,
  Section,
  SectionHeader,
  SectionOnTopTitle,
  SectionWithTitle,
  ThreeVCExpandable,
} from 'mm-ui-components';
import { Store } from 'redux';
import { css, StyleSheet } from 'aphrodite/no-important';
import { Theme } from '../../components/connectedComponents/Theme';
import { MetaTags } from '../../components/connectedComponents/MetaTags';
import { HeadScripts } from '../../components/connectedComponents/HeadScripts';
import { SECTION_NAMES, SLOT_NAMES } from '../templates.const';
import { LoadMoreButton, useFetchNextPosts } from '../../components/connectedComponents/LoadMoreButton';
import { Page } from '../Page';
import { getAdSlotId, getAdSlotOnSuccess } from '../../store/config/config.selectors';
import { ErrorReporter } from '../../errorReporting/errorReporter.type';
import { AppActions, AppState } from '../../store/initialStateRegistration';
import { useMeasureHydrateTime } from '../useMeasureHydrateTime';
import {
  getMetadataDescription,
  getMetadataTitle,
  getSectionArticlesByKey,
  getSectionImage,
  getSectionSubtitle,
  getSectionTitleByKey,
  getShowMorePaginationTextByKey,
  getShowMorePaginationURLByKey,
  getMetadataImage,
  getHreflangData,
  getPagination,
} from '../../store/template/feedPage/feedPageChicago/feedPageChicago.selectors';
import { NavigationLayout } from '../../components/connectedComponents/NavigationLayout';
import { getPageMetaText } from '../templates.utils';

interface FeedPageBangkokProps {
  store: Store<AppState, AppActions>;
  errorProvider: ErrorReporter;
}

const getArticlesInTemplate = (state: AppState) => {
  const feedSectionArticles = getSectionArticlesByKey(SECTION_NAMES.FEED_SECTION)(state) || [];
  return [
    ...feedSectionArticles,
  ];
};

export const FeedPageChicagoComponent: React.FunctionComponent<FeedPageBangkokProps> = ({ store, errorProvider }) => {
  useMeasureHydrateTime();
  return (
    <Page store={store} errorProvider={errorProvider}>
      <ErrorBoundary>
        <FeedPageChicagoContentConnected />
      </ErrorBoundary>
    </Page>
  );
};

const createStyle = () => StyleSheet.create({
  sectionsWrapper: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gridRowGap: `${baseUnit * 1.5}px`,
    marginBottom: `${baseUnit * 0.5}px`,
    justifyItems: 'center',
  },
});

export const FeedPageChicagoContentConnected: React.FunctionComponent = () => {
  const cardsSectionDisplayName = useSelector(getSectionTitleByKey(SECTION_NAMES.FEED_SECTION));
  const topSectionDisplayName = useSelector(getSectionTitleByKey(SECTION_NAMES.TOP_SECTION));
  const topSectionSubtitle = useSelector(getSectionSubtitle(SECTION_NAMES.TOP_SECTION));
  const topSectionImage = useSelector(getSectionImage(SECTION_NAMES.TOP_SECTION));
  const articles = useSelector(getArticlesInTemplate);
  const cardsSectionArticles = useSelector(getSectionArticlesByKey(SECTION_NAMES.FEED_SECTION));
  const metaTitle = useSelector(getMetadataTitle);
  const metaDescription = useSelector(getMetadataDescription);
  const pageImage = useSelector(getMetadataImage);
  const hreflangData = useSelector(getHreflangData) || [];
  const paginationData = useSelector(getPagination);
  const { prevButtonText, prevButtonURL, nextButtonText, nextButtonURL, currentPage } = paginationData;
  const oneOnOneId = useSelector(getAdSlotId(SLOT_NAMES.ONE_ON_ONE));
  const oneOnOneOnSuccess = useSelector(getAdSlotOnSuccess(SLOT_NAMES.ONE_ON_ONE));
  const topAdId = useSelector(getAdSlotId(SLOT_NAMES.TOP_AD));
  const topAdOnSuccess = useSelector(getAdSlotOnSuccess(SLOT_NAMES.TOP_AD));
  const belowSectionAdId = useSelector(getAdSlotId(SLOT_NAMES.BELOW_TOP_SECTION));
  const belowSectionAdOnSuccess = useSelector(getAdSlotOnSuccess(SLOT_NAMES.BELOW_TOP_SECTION));
  const style = createStyle();
  return (
    <React.Fragment>
      <MetaTags {...getPageMetaText(metaTitle, metaDescription, currentPage)} articles={articles} pageImage={pageImage} alternateUrls={hreflangData} />
      <HeadScripts />
      <Theme>
        <DisableMainCategory>
          <AppContent>
            <NavigationLayout mainWrapperConfigThemeKey="mainContent">
              <div className={css(style.sectionsWrapper)}>

                <Section themeConfigurationPropKey="firstSectionMaxWidth">
                  <SectionOnTopTitle title={topSectionDisplayName} subtitle={topSectionSubtitle} image={topSectionImage} />
                </Section>
                <SectionWithTitle>
                  <Ad id={topAdId} onSuccess={topAdOnSuccess} />
                  <SectionHeader
                    moreButtonSvg={null}
                    moreButtonText={null}
                    paddingFactors={{ large: 1, medium: 0.5, small: 1 }}
                  >
                    <SecondarySectionTitle>{cardsSectionDisplayName}</SecondarySectionTitle>
                  </SectionHeader>
                  <ThreeVCExpandable cards={cardsSectionArticles} paddingFactors={{ large: 1, medium: 0.5, small: 1 }} />
                </SectionWithTitle>

              </div>
              {Object.keys(paginationData).length === 0
                  ? <LoadMoreFeedPostsButtonConnected />
                  : <PaginationButtonGroup prevText={prevButtonText} prevURL={prevButtonURL} nextText={nextButtonText} nextURL={nextButtonURL} />}
              <Ad id={oneOnOneId} onSuccess={oneOnOneOnSuccess} />
              <Ad id={belowSectionAdId} onSuccess={belowSectionAdOnSuccess} />
            </NavigationLayout>
          </AppContent>
        </DisableMainCategory>
      </Theme>
    </React.Fragment>
  );
};

export const LoadMoreFeedPostsButtonConnected: React.FunctionComponent = () => {
  const sectionKey = SECTION_NAMES.FEED_SECTION;
  const shouldDisplay = !!useSelector(getShowMorePaginationURLByKey(sectionKey));
  const text = useSelector(getShowMorePaginationTextByKey(sectionKey));
  const onClick = useFetchNextPosts(sectionKey, getShowMorePaginationURLByKey);
  return <LoadMoreButton {...{ onClick, shouldDisplay, text }} />;
};
